import { useEffect, useState } from 'react';
import { Button, Row, Col, Select, TimePicker, App } from 'antd';
import { DayOfWeek, PowerHourConfig } from '../../../../dto/reporting-models';
import { reportingService } from '../../../../services/reporting-service';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

export const PowerHourConfigContainter = () => {
  const [powerHourConfigs, setPowerHourConfigs] = useState<PowerHourConfig[]>([]);
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    const fetchPowerHourConfigs = async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const configs = await reportingService.getPowerHourConfigs(token.accessToken);
      setPowerHourConfigs(configs);
    };

    fetchPowerHourConfigs();
  }, []);

  const addRow = () => {
    setPowerHourConfigs([...powerHourConfigs, { fromDayOfWeek: DayOfWeek.Monday, untilDayOfWeek: DayOfWeek.Monday, startTimeUtc: '', feId: crypto.randomUUID() } as PowerHourConfig]);
  };

  const updateRow = (updatedConfig: PowerHourConfig) => {
    const updatedConfigs = powerHourConfigs.map((config) => (config.feId === updatedConfig.feId ? updatedConfig : config));
    //this deletes any configs that have been added but removed before getting saved, aka temporary configs
    const updatedWithNoTemporary = updatedConfig.isDeleted && !updatedConfig.id ? updatedConfigs.filter((config) => config.feId !== updatedConfig.feId) : updatedConfigs;

    setPowerHourConfigs(updatedWithNoTemporary);
  };

  const saveChanges = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

    const hasInvalidConfig = powerHourConfigs.some((config) => {
      return !config.isDeleted && (isNaN(config.fromDayOfWeek) || isNaN(config.untilDayOfWeek) || !config.startTimeUtc || !config.endTimeUtc);
    });

    if (hasInvalidConfig) {
      notification.error({
        message: 'Validation Error',
        description: 'Please ensure all fields are filled out correctly.',
      });
      return;
    }

    const isUpdated = await reportingService.upsertPowerHourConfigs(token.accessToken, powerHourConfigs);

    isUpdated
      ? notification.success({
          message: 'Success',
          description: 'Power Hour Configs saved successfully.',
        })
      : notification.error({
          message: 'Error',
          description: 'Failed to save Power Hour Configs.',
        });
  };

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900 }}>
      <h2>Power Hour Config</h2>
      {powerHourConfigs
        .filter((x) => !x.isDeleted)
        .map((config, index) => (
          <Row key={index} gutter={16} style={{ marginBottom: 16 }}>
            <Col span={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="From Day"
                value={DayOfWeek[config.fromDayOfWeek]}
                onChange={(value) => updateRow({ ...config, fromDayOfWeek: DayOfWeek[value as keyof typeof DayOfWeek] })}
              >
                {Object.values(DayOfWeek)
                  .filter((x) => isNaN(Number(x)) && x != 'Sunday')
                  .map((day) => (
                    <Select.Option key={day} value={day}>
                      {day}
                    </Select.Option>
                  ))}
              </Select>
            </Col>
            <Col span={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Until Day"
                value={DayOfWeek[config.untilDayOfWeek]}
                onChange={(value) => updateRow({ ...config, untilDayOfWeek: DayOfWeek[value as keyof typeof DayOfWeek] })}
              >
                {Object.values(DayOfWeek)
                  .filter((x) => isNaN(Number(x)) && x != 'Sunday')
                  .map((day) => (
                    <Select.Option key={day} value={day}>
                      {day}
                    </Select.Option>
                  ))}
              </Select>
            </Col>
            <Col span={5}>
              <TimePicker
                placeholder="Start Time"
                value={config.startTimeUtc ? dayjs(`1970-01-01T${config.startTimeUtc}`) : null}
                onChange={(time) => updateRow({ ...config, startTimeUtc: time ? time.format('HH:mm') : '' })}
                format="HH:mm"
                minuteStep={15}
                style={{ width: '100%' }}
                suffixIcon={<ClockCircleOutlined />}
              />
            </Col>
            <Col span={5}>
              <TimePicker
                placeholder="End Time"
                value={config.endTimeUtc ? dayjs(`1970-01-01T${config.endTimeUtc}`) : null}
                onChange={(time) => updateRow({ ...config, endTimeUtc: time ? time.format('HH:mm') : '' })}
                format="HH:mm"
                minuteStep={15}
                style={{ width: '100%' }}
                suffixIcon={<ClockCircleOutlined />}
              />
            </Col>
            <Col span={4}>
              <Button onClick={() => updateRow({ ...config, isDeleted: true })}>Remove</Button>
            </Col>
          </Row>
        ))}
      <Row justify="space-between" style={{ marginTop: 16 }}>
        <Col>
          <Button onClick={addRow}>Add Row</Button>
        </Col>
        <Col>
          <Button type="primary" onClick={saveChanges}>
            Save Changes
          </Button>
        </Col>
      </Row>
    </div>
  );
};
